import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '../Button';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { cn } from '../../lib/cn';
import { DateRangeCalendar } from './DateRangeCalendar';
import {
  dateRangeValueToDraft,
  formatDateRangeLabel,
  formatDateValue,
  getInitialVisibleMonth,
  isSelectableDateRange,
  shiftSingleDayRange,
} from './DateRangePicker.helpers';
import {
  CalendarView,
  type DateRangePickerPreset,
  type DateRangePickerValue,
  type DateRangeDraft,
} from './DateRangePicker.types';

export interface DateRangePickerProps {
  label?: string | undefined;
  presets: DateRangePickerPreset[];
  value: DateRangePickerValue;
  onChange: (value: DateRangePickerValue) => void;
  className?: string | undefined;
  maxDate?: Date | undefined;
  popoverAlign?: 'start' | 'center' | 'end' | undefined;
  triggerClassName?: string | undefined;
  showDayNavigation?: boolean | undefined;
  previousDayLabel?: string | undefined;
  nextDayLabel?: string | undefined;
}

export function DateRangePicker({
  className,
  label = 'Date range',
  maxDate,
  onChange,
  popoverAlign = 'end',
  presets,
  previousDayLabel = 'Previous day',
  nextDayLabel = 'Next day',
  showDayNavigation = false,
  triggerClassName,
  value,
}: DateRangePickerProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [draftRange, setDraftRange] = useState<DateRangeDraft | undefined>(() =>
    dateRangeValueToDraft(value),
  );
  const [visibleMonth, setVisibleMonth] = useState(() => getInitialVisibleMonth(value));
  const [view, setView] = useState<CalendarView>(CalendarView.Days);

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      setPortalContainer(triggerRef.current?.closest('[role="dialog"]') ?? null);
      setDraftRange(dateRangeValueToDraft(value));
      setVisibleMonth(getInitialVisibleMonth(value));
      setView(CalendarView.Days);
    }
    setOpen(nextOpen);
  }

  function handleDateSelect(date: Date) {
    setDraftRange((current) => {
      if (!current?.from || current.to) return { from: date };
      if (date < current.from) return { from: date, to: current.from };
      return { from: current.from, to: date };
    });
  }

  function handlePreset(preset: DateRangePickerPreset) {
    const nextRange = preset.getValue();
    if (!isSelectableDateRange(nextRange, maxDate)) return;
    onChange({ ...nextRange, preset: preset.key });
    setDraftRange(dateRangeValueToDraft(nextRange));
    setVisibleMonth(getInitialVisibleMonth(nextRange));
    setOpen(false);
  }

  function handleApply() {
    if (!draftRange?.from || !draftRange.to) return;
    const dateFrom = formatDateValue(draftRange.from);
    const dateTo = formatDateValue(draftRange.to);
    if (!isSelectableDateRange({ dateFrom, dateTo }, maxDate)) return;
    onChange(withMatchingPreset({ dateFrom, dateTo }));
    setOpen(false);
  }

  function handleClear() {
    setDraftRange(undefined);
    onChange({ dateFrom: '', dateTo: '', preset: '' });
    setOpen(false);
  }

  const canApply = Boolean(
    draftRange?.from &&
    draftRange.to &&
    isSelectableDateRange(
      {
        dateFrom: formatDateValue(draftRange.from),
        dateTo: formatDateValue(draftRange.to),
      },
      maxDate,
    ),
  );
  const previousDay = shiftSingleDayRange(value, -1, maxDate);
  const nextDay = shiftSingleDayRange(value, 1, maxDate);

  // Dates alone decide the preset, so stepping a day from Today lands on Yesterday, not a custom range.
  function withMatchingPreset(range: { dateFrom: string; dateTo: string }) {
    const matches = presets.filter((preset) => {
      const presetRange = preset.getValue();
      return presetRange.dateFrom === range.dateFrom && presetRange.dateTo === range.dateTo;
    });
    const match = matches.find((preset) => preset.key === value.preset) ?? matches[0];
    return { ...range, preset: match?.key ?? '' };
  }

  return (
    <section className={cn('flex min-w-0 items-center justify-end gap-2', className)}>
      {showDayNavigation ? (
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={!previousDay}
          aria-label={previousDayLabel}
          onClick={() => {
            if (previousDay) onChange(withMatchingPreset(previousDay));
          }}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      ) : null}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            className={cn(
              'border-input bg-card text-foreground hover:border-ring focus-visible:ring-ring/50',
              'flex h-9 w-full max-w-[360px] cursor-pointer items-center justify-between rounded-md border px-3 text-left shadow-[var(--shadow-control)] transition-colors focus-visible:ring-[3px] focus-visible:outline-none',
              triggerClassName,
            )}
            aria-label={`${label} ${formatDateRangeLabel(value)}`}
          >
            <span className="flex min-w-0 items-center gap-2.5">
              <CalendarDays className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="block min-w-0 truncate text-sm font-medium">
                {formatDateRangeLabel(value)}
              </span>
            </span>
            <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align={popoverAlign}
          portalContainer={portalContainer}
          className={cn(
            'overflow-hidden p-0',
            presets.length ? 'w-[min(92vw,460px)]' : 'w-[min(92vw,280px)]',
          )}
        >
          <div className={cn('grid', presets.length && 'md:grid-cols-[180px_280px]')}>
            {presets.length ? (
              <PresetList
                maxDate={maxDate}
                onPresetSelect={handlePreset}
                presets={presets}
                selected={value.preset}
              />
            ) : null}
            <div className="flex min-w-0 justify-center">
              <DateRangeCalendar
                draftRange={draftRange}
                maxDate={maxDate}
                onDateSelect={handleDateSelect}
                onViewChange={setView}
                onVisibleMonthChange={setVisibleMonth}
                view={view}
                visibleMonth={visibleMonth}
              />
            </div>
          </div>
          <div className="border-border flex items-center justify-end gap-3 border-t p-3">
            <Button
              type="button"
              variant="ghost"
              className="rounded-full px-5"
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button
              type="button"
              className="rounded-full px-6"
              disabled={!canApply}
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {showDayNavigation ? (
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={!nextDay}
          aria-label={nextDayLabel}
          onClick={() => {
            if (nextDay) onChange(withMatchingPreset(nextDay));
          }}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      ) : null}
    </section>
  );
}

function PresetList({
  maxDate,
  onPresetSelect,
  presets,
  selected,
}: {
  maxDate: Date | undefined;
  onPresetSelect: (preset: DateRangePickerPreset) => void;
  presets: DateRangePickerPreset[];
  selected: string | undefined;
}) {
  return (
    <div className="border-border flex flex-col border-b p-2 md:border-r md:border-b-0">
      {presets.map((preset) => (
        <button
          key={preset.key}
          disabled={!isSelectableDateRange(preset.getValue(), maxDate)}
          type="button"
          className={cn(
            'text-muted-foreground hover:text-foreground cursor-pointer rounded-sm px-2 py-1.5 text-left text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-50',
            selected === preset.key && 'text-foreground bg-accent',
          )}
          onClick={() => {
            onPresetSelect(preset);
          }}
        >
          {preset.label}
        </button>
      ))}
    </div>
  );
}
